import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { MultiFactorResolver, UserCredential } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { authenticateWithMfa, mfaError } from '../common/mfa';
import MfaChallenge from './MfaChallenge';
import { mfaStyles as styles } from './mfaStyles';
import { useMfaDialog } from './useMfaDialog';

function requestMfaChallenge(resolver: MultiFactorResolver): Promise<UserCredential> {
    return new Promise((resolve, reject) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);
        const finish = (credential?: UserCredential) => {
            root.unmount();
            container.remove();
            if (credential)
                resolve(credential);
            else
                reject(new Error('Sign-in canceled.'));
        };
        root.render(<MfaChallenge resolver={resolver} onComplete={credential => finish(credential)} onCancel={() => finish()} />);
    });
}

export const signInWithMfa = (email: string, password: string) => authenticateWithMfa(auth, email, password, requestMfaChallenge);

function PasswordCheck({ onComplete, onCancel }: { onComplete: () => void; onCancel: () => void }) {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const panel = useMfaDialog(onCancel, busy);
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setBusy(true);
        setError('');
        try {
            await authenticateWithMfa(auth, auth.currentUser?.email || '', password, requestMfaChallenge, true);
            setPassword('');
            onComplete();
        } catch (reason) {
            setError(mfaError(reason));
            setBusy(false);
        }
    };
    return <div className={styles.overlay}><section ref={panel} role="dialog" aria-modal="true" aria-labelledby="mfa-password-title" className={styles.panel}>
        <h2 id="mfa-password-title" className="text-xl font-bold">Confirm account security</h2>
        <p className={styles.text}>Verify your password and MFA before changing security settings.</p>
        {error && <p role="alert" className={styles.error}>{error}</p>}
        <form onSubmit={submit} className="space-y-4"><label className="block text-sm font-semibold">Password<input autoFocus type="password" autoComplete="current-password" className={styles.input} value={password} onChange={event => setPassword(event.target.value)} required disabled={busy} /></label>
            <div className="flex gap-3"><button disabled={busy} className={styles.button}>{busy ? 'Verifying…' : 'Continue'}</button><button type="button" disabled={busy} className={styles.secondary} onClick={onCancel}>Cancel</button></div>
        </form>
    </section></div>;
}

export function requestRecentMfa(): Promise<void> {
    return new Promise((resolve, reject) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);
        const finish = (success: boolean) => {
            root.unmount();
            container.remove();
            if (success)
                resolve();
            else
                reject(new Error('Security change canceled.'));
        };
        root.render(<PasswordCheck onComplete={() => finish(true)} onCancel={() => finish(false)} />);
    });
}
